"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import DocumentCardsView, {
  type DocumentCard,
} from "@/components/optilase/hub/document-cards-view";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { TextLink } from "@/components/optilase/text-link";
import { accentOn, headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { useScrollNeedsFocus } from "@/lib/use-scrollable";
import { cn } from "@/lib/utils";
import { headingLines } from "@/lib/heading-lines";

export type InfoCard = {
  eyebrow?: string;
  title: string;
  body?: React.ReactNode | React.ReactNode[];
  linkLabel?: string;
  href?: string;
};

export type InfoCardsViewProps = {
  heading: string;
  intro?: string;
  linkLabel?: string;
  linkHref?: string;
  cards: InfoCard[];
  documents?: DocumentCard[];
  documentColumns?: string;
  footnote?: string;
  layout?: "beside" | "grid" | "grid_3" | "image";
  image?: string;
  imageAlt?: string;
  imageFocus?: string;
  surface?: Surface | null;
  cardSurface?: Surface | null;
};

// A scrolling body needs a tab stop so it can be reached by keyboard, but only
// when it actually scrolls; otherwise it is a focus stop on plain text.
function CardBody({ carousel, children }: { carousel: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !carousel) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [carousel, children]);

  return (
    <div
      ref={ref}
      tabIndex={carousel && overflows ? 0 : undefined}
      className={cn(
        carousel &&
          "h-full scrollbar-none overflow-y-auto pb-6 outline-none lg:overflow-visible lg:pb-0",
        carousel && overflows && "focus-visible:ring-teal-07/40 focus-visible:ring-2",
      )}
    >
      {children}
    </div>
  );
}

export default function InfoCardsView({
  heading,
  intro,
  linkLabel,
  linkHref = "#",
  cards,
  documents = [],
  documentColumns,
  footnote,
  layout = "beside",
  image,
  imageAlt = "",
  imageFocus,
  surface,
  cardSurface,
}: InfoCardsViewProps) {
  const beside = layout === "beside";
  const withImage = layout === "image";
  // Never open more columns than there are cards, or a lone card is left stranded.
  const maxCols = layout === "grid_3" ? 3 : 2;
  const cols = Math.min(cards.length, maxCols);
  const gridCols =
    cols >= 3 ? "lg:grid-cols-3" : cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1 lg:max-w-200";
  // A filled card sets its own contrast, independent of the section behind it.
  const cardOn = cardSurface ?? surface;
  const cardBg = cardSurface?.backgroundColor;
  const filled =
    withImage &&
    (!surface || surface.backgroundColor.toLowerCase() !== "#ffffff"
      ? "border-transparent bg-white"
      : "border-transparent bg-teal-01");
  const fadeFrom = cardBg ?? surface?.backgroundColor ?? "#ffffff";
  const carousel = !beside && !withImage;
  const scroller = useRef<HTMLDivElement>(null);
  const isScrollable = useScrollNeedsFocus(scroller);
  const scrolls = isScrollable && !beside && !withImage;
  const [page, setPage] = useState(0);
  const sync = useCallback(() => {
    const el = scroller.current;
    if (el) setPage(activeSlide(el));
  }, []);

  const bodyClass = onSurface(cardOn, "text-white/85", "text-grey-08", "text-grey-08");

  const documentList =
    documents.length > 0 ? (
      <DocumentCardsView
        cards={documents}
        columns={documentColumns}
        label={heading}
        surface={surface}
        cardSurface={cardSurface}
      />
    ) : null;

  const list = (
    <div
      ref={beside || withImage ? undefined : scroller}
      onScroll={beside || withImage ? undefined : sync}
      tabIndex={scrolls ? 0 : -1}
      role={scrolls ? "region" : undefined}
      aria-label={scrolls ? heading : undefined}
      className={cn(
        beside
          ? "flex flex-col gap-4"
          : withImage
            ? // 12px apart on the phone, 16 from desktop (1:12777, 1:12353).
              "flex flex-col gap-3 lg:gap-4"
            : `-mx-5 flex snap-x snap-mandatory scroll-pl-5 scrollbar-none gap-4 overflow-x-auto px-5 pb-2 md:-mx-10 md:scroll-pl-10 md:px-10 lg:mx-0 lg:grid ${gridCols} lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0`,
      )}
    >
      {cards.map((card) => (
        <div
          key={card.title}
          className={cn(
            "flex flex-col rounded-2xl border",
            beside
              ? "gap-2 p-6"
              : withImage
                ? "gap-2 p-5 lg:p-6"
                : "max-h-[26rem] w-[286px] shrink-0 snap-start gap-4 p-6 lg:max-h-none lg:w-auto lg:p-8",
            cardBg
              ? "border-transparent"
              : filled || (surface ? "border-current/15" : "border-grey-01"),
          )}
          style={cardBg ? { backgroundColor: cardBg } : undefined}
        >
          {card.eyebrow ? (
            <p
              className={cn(
                "text-sm leading-snug font-medium",
                onSurface(cardOn, "text-white/70", "text-grey-06", "text-grey-06"),
              )}
            >
              {card.eyebrow}
            </p>
          ) : null}
          <h3
            className={cn(
              "text-h5 font-semibold tracking-[-0.01em]",
              beside
                ? accentOn(cardOn, "text-teal-07")
                : withImage
                  ? accentOn(cardOn, "text-teal-08")
                  : onSurface(cardOn, "text-white", "text-grey-09", "text-grey-09"),
            )}
          >
            {card.title}
          </h3>
          {card.body ? (
            <div className={cn("relative", carousel && "min-h-0 flex-1")}>
              <CardBody carousel={carousel}>
                {Array.isArray(card.body) ? (
                  <div className="flex flex-col gap-6">
                    {card.body.map((para, i) => (
                      <p
                        key={i}
                        className={cn("text-base leading-relaxed tracking-[-0.01em]", bodyClass)}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className={cn("text-base leading-relaxed tracking-[-0.01em]", bodyClass)}>
                    {card.body}
                  </p>
                )}
              </CardBody>
              {carousel ? (
                <span
                  aria-hidden
                  style={{ backgroundImage: `linear-gradient(to top, ${fadeFrom}, transparent)` }}
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-10 lg:hidden"
                />
              ) : null}
            </div>
          ) : null}
          {card.linkLabel ? (
            <TextLink
              href={card.href ?? "#"}
              className={cn(beside ? "" : "mt-auto", accentOn(cardOn, "text-teal-08"))}
            >
              {card.linkLabel}
            </TextLink>
          ) : null}
        </div>
      ))}
    </div>
  );

  const header = (
    <div className="flex flex-col gap-5">
      <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
        {headingLines(heading)}
      </h2>
      {intro ? (
        <p
          className={cn(
            "max-w-[943px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl",
            bodyClass,
          )}
        >
          {intro}
        </p>
      ) : null}
      {linkLabel ? (
        <TextLink href={linkHref} className={cn("mt-1", accentOn(surface, "text-teal-08"))}>
          {linkLabel}
        </TextLink>
      ) : null}
    </div>
  );

  if (withImage) {
    return (
      <section
        style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
        className={cn(
          "px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20",
          surface ? "" : "bg-teal-02",
        )}
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-8 lg:grid lg:grid-cols-[minmax(0,556px)_minmax(0,640px)] lg:justify-between lg:gap-10">
          <div className="order-first h-[310px] w-full overflow-hidden rounded-[20px] md:h-[380px] lg:order-0 lg:col-start-2 lg:my-10 lg:h-auto lg:min-h-[400px]">
            <AssetImage
              src={image ?? ""}
              focus={imageFocus}
              alt={imageAlt}
              width={640}
              height={571}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="flex flex-col gap-5 lg:col-start-1 lg:row-start-1 lg:gap-6">
            <h2 className={cn("text-section lg:max-w-[414px]", headingOn(surface, "text-teal-08"))}>
              {headingLines(heading)}
            </h2>
            {intro ? (
              <p
                className={cn("text-base leading-relaxed tracking-[-0.01em] lg:text-lg", bodyClass)}
              >
                {intro}
              </p>
            ) : null}
            {list}
            {documentList}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div
        className={cn(
          "mx-auto max-w-7xl",
          beside
            ? "grid gap-8 lg:grid-cols-[minmax(0,540px)_minmax(0,640px)] lg:justify-between lg:gap-25"
            : "flex flex-col gap-8 lg:gap-10",
        )}
      >
        {header}
        <div className="flex flex-col">
          {list}
          {!beside && cards.length > 1 ? (
            <CarouselDots
              count={cards.length}
              active={page}
              label="card"
              onSelect={(i) => {
                const card = scroller.current?.children[i] as HTMLElement | undefined;
                card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
              }}
              className="lg:hidden"
            />
          ) : null}
          {documentList ? <div className="mt-6 lg:mt-8">{documentList}</div> : null}
          {footnote ? (
            <p className={cn("mt-6 text-base leading-relaxed tracking-[-0.01em]", bodyClass)}>
              {footnote}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
